import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { BarChart3, PieChart as PieChartIcon } from "lucide-react";
import { formatCurrency, MODE_COLORS } from "./paymentsConstants";

const MODE_KEY_MAP = { cash: "Cash", card: "Card", credit: "Credit", credit_only: "Credit", self_credit: "Self Credit", eft: "EFT", upi: "UPI", neft_rtgs: "NEFT/RTGS", wallet: "Advance", payout: "Payout", others: "Others", collection: "Collection", debtors: "Debtors", advances: "Advances" };

export function PaymentCharts({ chartView, setChartView, barData, pieData, grandTotals, modeFilter }) {
  // Pie chart can't render 0-value slices, so filter those out
  const pieChartData = modeFilter
    ? pieData.filter(d => d.name === MODE_KEY_MAP[modeFilter] && d.value > 0)
    : pieData.filter(d => d.value > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200/60 shadow-sm p-5" data-testid="bar-chart-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-800">
            {chartView === "bar"
              ? modeFilter ? `${MODE_KEY_MAP[modeFilter]} Collection by Date` : "Collection by Date"
              : "Payment Mode Distribution"}
          </h3>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setChartView("bar")} className={`p-1.5 rounded-md transition-all ${chartView === "bar" ? "bg-white shadow-sm text-slate-800" : "text-slate-400 hover:text-slate-600"}`} data-testid="chart-toggle-bar">
              <BarChart3 className="w-4 h-4" />
            </button>
            <button onClick={() => setChartView("pie")} className={`p-1.5 rounded-md transition-all ${chartView === "pie" ? "bg-white shadow-sm text-slate-800" : "text-slate-400 hover:text-slate-600"}`} data-testid="chart-toggle-pie">
              <PieChartIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        {chartView === "bar" ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={barData} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => v >= 100000 ? `${(v/100000).toFixed(0)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {(!modeFilter || modeFilter === "cash") && <Bar dataKey="Cash" stackId="a" fill={MODE_COLORS.Cash} opacity={modeFilter && modeFilter !== "cash" ? 0.15 : 1} />}
              {(!modeFilter || modeFilter === "card") && <Bar dataKey="Card" stackId="a" fill={MODE_COLORS.Card} opacity={modeFilter && modeFilter !== "card" ? 0.15 : 1} />}
              {(!modeFilter || modeFilter === "credit") && <Bar dataKey="Credit" stackId="a" fill={MODE_COLORS.Credit} opacity={modeFilter && modeFilter !== "credit" ? 0.15 : 1} />}
              {(!modeFilter || modeFilter === "upi") && <Bar dataKey="UPI" stackId="a" fill={MODE_COLORS.UPI} opacity={modeFilter && modeFilter !== "upi" ? 0.15 : 1} />}
              {(!modeFilter || modeFilter === "eft") && <Bar dataKey="EFT" stackId="a" fill={MODE_COLORS.EFT} opacity={modeFilter && modeFilter !== "eft" ? 0.15 : 1} />}
              {(!modeFilter || modeFilter === "neft_rtgs") && <Bar dataKey="NEFT/RTGS" stackId="a" fill={MODE_COLORS["NEFT/RTGS"]} opacity={modeFilter && modeFilter !== "neft_rtgs" ? 0.15 : 1} />}
              {(!modeFilter || modeFilter === "wallet" || modeFilter === "advances") && <Bar dataKey="Advance" stackId="a" fill={MODE_COLORS.Advance || "#f59e0b"} opacity={modeFilter && modeFilter !== "wallet" && modeFilter !== "advances" ? 0.15 : 1} />}
              {(!modeFilter || modeFilter === "payout") && <Bar dataKey="Payout" stackId="a" fill={MODE_COLORS.Payout} opacity={modeFilter && modeFilter !== "payout" ? 0.15 : 1} />}
              {(!modeFilter || modeFilter === "others") && <Bar dataKey="Others" stackId="a" fill={MODE_COLORS.Others} opacity={modeFilter && modeFilter !== "others" ? 0.15 : 1} radius={[4,4,0,0]} />}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={pieChartData} cx="50%" cy="50%" innerRadius={65} outerRadius={110} paddingAngle={3} dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: "#cbd5e1" }}>
                {pieChartData.map((entry) => <Cell key={entry.name} fill={MODE_COLORS[entry.name] || "#94a3b8"} />)}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm p-5" data-testid="pie-chart-section">
        <h3 className="text-sm font-bold text-slate-800 mb-4">Mode Breakdown</h3>
        <div className="space-y-2.5">
          {pieData.map((entry) => {
            const pct = grandTotals.total > 0 ? ((entry.value / grandTotals.total) * 100) : 0;
            const isActive = modeFilter === (entry.name.toLowerCase() === "others" ? "others" : entry.name.toLowerCase());
            const isDimmed = modeFilter && !isActive;
            return (
              <div key={entry.name} className={`group transition-opacity ${isDimmed ? "opacity-30" : ""}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: MODE_COLORS[entry.name] || "#94a3b8" }} />
                    <span className={`text-sm font-medium ${isActive ? "text-indigo-700 font-bold" : "text-slate-600"}`}>{entry.name}</span>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-bold ${isActive ? "text-indigo-700" : "text-slate-800"}`}>{formatCurrency(entry.value)}</span>
                    <span className="text-xs text-slate-400 ml-2">{pct.toFixed(1)}%</span>
                  </div>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5">
                  <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: MODE_COLORS[entry.name] || "#94a3b8" }} />
                </div>
              </div>
            );
          })}
          {pieData.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No data available</div>}
        </div>
      </div>
    </div>
  );
}
